import './TextSelectInput.scss';

import * as React from 'react';

import cx from 'classnames';

import { FocusIndicator } from '../FocusIndicator/FocusIndicator';

interface TextSelectOption<Value> {
  text: string;
  value: Value;
}

type TextSelectValue<Value> = Value | TextSelectOption<Value>;

interface TextSelectProps<Value> {
  className?: string;
  id: string;
  /** Used for `aria-label` attr, and displayed as an `optgroup` heading when open. */
  label: string;
  /** Available select options. */
  options: TextSelectValue<Value>[];
  onChange: (value: Value) => void;
  /** The currently selected option. */
  value: Value;
}

function SelectIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12">
      <path
        className="fill-grey-800"
        fillRule="nonzero"
        d="M8 7.25a.75.75 0 01.676 1.076l-.052.09-2 3a.75.75 0 01-1.18.089l-.068-.089-2-3a.75.75 0 01.52-1.16L4 7.25h4zM5.376.584a.75.75 0 011.18-.089l.068.089 2 3a.75.75 0 01-.52 1.16L8 4.75H4a.75.75 0 01-.676-1.076l.052-.09 2-3z"
      />
    </svg>
  );
}

function parseSimpleToComplexOption<Value>(
  option: TextSelectValue<Value>
): TextSelectOption<Value> {
  if (
    typeof option === 'object' &&
    option !== null &&
    'text' in option &&
    'value' in option
  ) {
    return option as TextSelectOption<Value>;
  }
  return { value: option as Value, text: String(option) };
}

/**
 * A custom `select` input that inherits its style from the
 * surrounding text.
 *
 * The `options` prop accepts an array of strings, numbers, or
 * objects consisting of a `value` key (which can be either a
 * string or a number) and a `text` key (which can be a string).
 */
export function TextSelectInput<Value>({
  className,
  id,
  label,
  options,
  onChange,
  value,
}: TextSelectProps<Value>): React.ReactElement {
  const parsedOptions = options.map((option) =>
    parseSimpleToComplexOption<Value>(option)
  );
  const [currentText] = parsedOptions.filter((o) => value === o.value);

  if (!currentText || !currentText.text) {
    throw new Error(
      `The provided value of "${value}" does not exist in the provided \`options\` list.`
    );
  }

  const containerClass = cx(
    'relative inline-block rounded-2 text-select-input',
    {
      [className]: !!className,
    }
  );

  return (
    <div className={containerClass}>
      <div className="flex items-center pointer-events-none select-none">
        {currentText.text}{' '}
        <div className="flex pl-1">
          <SelectIcon />
        </div>
      </div>
      <select
        className="absolute inset-0 w-full opacity-0 cursor-pointer"
        id={id}
        value={String(value)}
        onChange={(ev: React.FormEvent<HTMLSelectElement>) => {
          if (typeof onChange === 'function') {
            const newValue =
              parsedOptions[ev.currentTarget.selectedIndex].value;
            onChange(newValue);
          }
        }}
        title={label}
        aria-label={label}
      >
        <optgroup label={label}>
          {parsedOptions.map((option, index) => (
            <option
              value={String(option.value)}
              key={`${option.value}-${index}`}
            >
              {option.text}
            </option>
          ))}
        </optgroup>
      </select>

      <FocusIndicator notOffset isInset />
    </div>
  );
}
